import React, { useState, useEffect } from 'react';
import { taskboardService, SavedTaskBoard } from '../services/taskboardService';

interface TaskboardSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (boardId: string | null) => void;
}

const TaskboardSelectionModal: React.FC<TaskboardSelectionModalProps> = ({ isOpen, onClose, onConfirm }) => {
    const [boards, setBoards] = useState<SavedTaskBoard[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
    const [createNew, setCreateNew] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadBoards();
            setCreateNew(false);
            setSelectedBoardId(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!createNew && boards.length > 0 && !selectedBoardId) {
            setSelectedBoardId(boards[0].id);
        }
    }, [boards, createNew, selectedBoardId]);

    const loadBoards = async () => {
        setLoading(true);
        try {
            const data = await taskboardService.getTaskBoards();
            setBoards(data);
        } catch (error) {
            console.error('Error loading taskboards:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = () => {
        if (createNew) {
            onConfirm(null);
        } else if (selectedBoardId) {
            onConfirm(selectedBoardId);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div className="modal-content" style={{
                background: 'white',
                padding: '24px',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '400px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}>
                <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.25rem', fontWeight: 600 }}>Add to Taskboard</h2>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            checked={createNew}
                            onChange={() => { setCreateNew(true); setSelectedBoardId(null); }}
                            style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontWeight: 500 }}>Create New Taskboard</span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            checked={!createNew}
                            onChange={() => { setCreateNew(false); if (boards.length > 0) setSelectedBoardId(boards[0].id); }}
                            style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontWeight: 500 }}>Select Existing Taskboard</span>
                    </label>

                    {!createNew && (
                        <div style={{ marginLeft: '24px', marginTop: '8px' }}>
                            {loading ? (
                                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading boards...</p>
                            ) : boards.length === 0 ? (
                                <p style={{ color: '#ef4444', fontSize: '0.875rem' }}>No existing taskboards found.</p>
                            ) : (
                                <select
                                    value={selectedBoardId || ''}
                                    onChange={(e) => setSelectedBoardId(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {boards.map(board => (
                                        <option key={board.id} value={board.id}>
                                            {board.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px 16px',
                            background: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#374151'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!createNew && !selectedBoardId}
                        style={{
                            padding: '8px 16px',
                            background: '#2563eb',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: 'white',
                            opacity: (!createNew && !selectedBoardId) ? 0.5 : 1
                        }}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskboardSelectionModal;
